import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, Users, Award, TrendingUp, Shield, Linkedin, Mail } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import PageHero from '../components/layout/PageHero';
import CTASection from '../components/home/CTASection';

const values = [
  {
    icon: Users,
    title: "Persönlich",
    description: "Jede Transaktion wird von erfahrenen Beratern begleitet – keine Call-Center, keine Anonymität."
  },
  {
    icon: Shield,
    title: "Vertraulich",
    description: "Absolute Diskretion bei allen Mandaten. Ihre Unternehmensdaten sind bei uns sicher."
  },
  {
    icon: Award,
    title: "Erfahren",
    description: "Über 10 Jahre Erfahrung und CHF 400+ Mio. begleitetes Transaktionsvolumen."
  },
  {
    icon: TrendingUp,
    title: "Erfolgsorientiert",
    description: "Wir arbeiten auf Ihren Erfolg hin – mit fairen, transparenten Konditionen."
  }
];

const UeberUnsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Über uns – Schweizer Nachfolge Experten AG</title>
        <meta name="description" content="unternehmenbewerten.ch ist eine Entwicklung der Schweizer Nachfolge Experten AG. Über 10 Jahre Erfahrung in Unternehmensbewertung, M&A Beratung und Nachfolgeplanung für Schweizer KMU." />
        <link rel="canonical" href="https://www.unternehmenbewerten.ch/ueber-uns" />
      </Helmet>
      
      <Header />
      
      <main className="pt-20">
        {/* Hero */}
        <PageHero
          title="Über uns"
          subtitle="unternehmenbewerten.ch ist eine Entwicklung der Schweizer Nachfolge Experten AG – Ihrem Partner für Unternehmensbewertung, Nachfolgeplanung und M&A-Transaktionen in der Schweiz."
          breadcrumb="Über uns"
        />

        {/* Main About Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Intro Text */}
            <div className="text-center space-y-6 mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a1c1b]">
                Die Schweizer Nachfolge Experten AG
              </h2>
              <p className="text-[#5a5d5b] leading-relaxed text-lg">
                Die Schweizer Nachfolge Experten AG ist eines der führenden Beratungsunternehmen 
                für Unternehmenstransaktionen in der Schweiz. Seit über 10 Jahren begleiten wir 
                Unternehmerinnen und Unternehmer beim Kauf und Verkauf von KMU.
              </p>
              <p className="text-[#5a5d5b] leading-relaxed">
                Im Dreh- und Angelpunkt der Schweizer KMU-Landschaft verbinden wir fundierte 
                Bewertungsexpertise mit praktischer Transaktionserfahrung. Unser Ziel: Erfolgreiche 
                Übergaben, die für alle Beteiligten stimmen – Verkäufer, Käufer und Unternehmen.
              </p>
              <p className="text-[#5a5d5b] leading-relaxed">
                Mit <strong>unternehmenbewerten.ch</strong> haben wir eine digitale Plattform geschaffen, 
                die Schweizer KMU-Inhabern eine erste, kostenlose Wertindikation ermöglicht. Diese 
                Bewertung dient als Orientierungshilfe und ersetzt keine fachmännisch durchgeführte 
                Unternehmensbewertung – bietet aber einen wertvollen ersten Schritt.
              </p>
            </div>

            {/* Schweizer Nachfolge Logo & CTA */}
            <div className="bg-[#f8f9fa] rounded-2xl p-8 border border-gray-100 text-center mb-16">
              <img 
                src="/schweizer-nachfolge-logo.png" 
                alt="Schweizer Nachfolge Experten AG" 
                className="h-16 mx-auto mb-6"
              />
              <p className="text-[#5a5d5b] mb-6">
                Erfahren Sie mehr über unsere Dienstleistungen und unser Team.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://www.schweizernachfolge.ch" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary gap-2"
                >
                  Schweizer Nachfolge besuchen
                  <ArrowRight size={18} />
                </a>
                <Link to="/kontakt" className="btn-secondary gap-2">
                  Kontakt aufnehmen
                </Link>
              </div>
            </div>

            {/* Values */}
            <div className="mb-16">
              <h3 className="text-xl font-bold text-[#1a1c1b] text-center mb-8">Unsere Werte</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {values.map((value) => (
                  <div key={value.title} className="flex gap-4 p-6 bg-[#f8f9fa] rounded-xl">
                    <div className="w-12 h-12 rounded-xl bg-[#FFE5E5] flex items-center justify-center flex-shrink-0">
                      <value.icon className="w-6 h-6 text-[#FF0000]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#1a1c1b] mb-1">{value.title}</h4>
                      <p className="text-[#5a5d5b] text-sm">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Expert Section */}
            <div className="max-w-md mx-auto bg-[#f8f9fa] rounded-2xl p-8 border border-gray-100 text-center mb-16">
              <h3 className="text-xl font-bold text-[#1a1c1b] mb-2">Unser Experte</h3>
              <p className="text-[#5a5d5b] text-sm mb-8">
                Wir kümmern uns gerne um alle Fragen rund um eine Unternehmenstransaktion.
              </p>
              
              <div className="flex flex-col items-center">
                <div className="w-52 h-52 rounded-2xl overflow-hidden bg-white border-4 border-white shadow-2xl mb-6 cursor-pointer transform hover:scale-110 hover:-translate-y-2 transition-all duration-500 ease-out">
                  <img 
                    src="/christoph-jermann.webp" 
                    alt="Christoph Jermann" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <h4 className="text-xl font-bold text-[#1a1c1b]">Christoph Jermann</h4>
                <p className="text-[#FF0000] font-medium text-sm mt-1">Chief Development Officer (CDO)</p>
                <p className="text-[#5a5d5b] text-sm mt-1">
                  Senior Consultant<br />
                  Schweizer Nachfolge Experten AG
                </p>
                
                {/* LinkedIn & Email Links */}
                <div className="flex items-center gap-4 mt-4">
                  <a 
                    href="https://www.linkedin.com/in/christoph-jermann-065526205/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white rounded-lg hover:bg-[#005885] transition-colors text-sm font-medium"
                    title="LinkedIn Profil"
                  >
                    <Linkedin size={16} />
                    LinkedIn
                  </a>
                  <a 
                    href="mailto:ch.jermann@schweizernachfolge.ch"
                    className="flex items-center gap-2 px-4 py-2 bg-[#FF0000] text-white rounded-lg hover:bg-[#CC0000] transition-colors text-sm font-medium"
                    title="E-Mail senden"
                  >
                    <Mail size={16} />
                    E-Mail
                  </a>
                </div>
                <p className="text-[#5a5d5b] text-xs mt-2">
                  ch.jermann@schweizernachfolge.ch
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <CTASection />
      </main>
      
      <Footer />
    </div>
  );
};

export default UeberUnsPage;
